import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {FormattedMessage, useIntl} from 'react-intl';

import {useGetTranslations} from 'utils/hooks/useGetTranslations';
import {IStore} from 'redux/interface';
import {getMaterialsContent, getTranslations, materialsAsyncActions, sendMaterials} from 'redux/pageAsyncActions';
import {removeMaterialAction} from 'redux/content/contentAsyncActions';
import {IArticleCard} from 'redux/content/interfaces';
import {TranslationKeys} from 'utils/constants/routeConstants';

import NotFoundImg from 'assets/image/common/notFound.png';
import DefaultIcon from 'assets/image/common/default.png';

import CardSection from 'components/card-section';
import Loader from 'components/loader';
import NotFoundInfo from 'components/not-found-info';
import {IShareMaterials} from 'components/materials-share/MaterialsShare';
import MaterialsShare from 'components/materials-share';
import {setSendMaterialsStatus} from '../../redux/content/actions';

import './styles.scss';

export const Materials: React.FunctionComponent = () => {
  const {
    materialsContent,
    sendMaterialsStatus
  } = useSelector((state: IStore) => state.content);
  const {
    loading
  } = useSelector((state: IStore) => state.status);
  const dispatch = useDispatch();
  const history = useNavigate();
  const intl = useIntl();
  const [isLoadCards, setIsLoadCards] = useState(true);
  const [isShareMode, setIsShareMode] = useState(false);
  const [checkedCards, setCheckedCards] = useState<number[]>([]);
  const [recipientInfo, setRecipientInfo] = useState<IShareMaterials>({
    emails: '',
    emailHtml: '',
    templateId: 0,
    toSelf: false
  });

  useGetTranslations({
    translationKey: TranslationKeys.Materials
  });

  useEffect(() => {
    dispatch(materialsAsyncActions[getMaterialsContent]());
    setIsLoadCards(false);
  }, []);

  useEffect(() => {
    setCheckedCards([]);
    setIsShareMode(false);
  }, [sendMaterialsStatus]);

  useEffect(() => {
    if (!isShareMode) {
      setCheckedCards([]);
      setRecipientInfo({
        emails: '',
        emailHtml: '',
        templateId: 0,
        toSelf: false
      });
    }
  }, [isShareMode]);

  function removeAll() {
    const cardsId: number[] = [];

    materialsContent.map((card: IArticleCard) => {
      return cardsId.push(card.id);
    });
    dispatch(removeMaterialAction(cardsId, true));
  }

  function validateModel(): boolean {
    return Boolean(checkedCards.length && recipientInfo.emails && recipientInfo.templateId);
  }

  function handleSendMaterials() {
    if (validateModel()) {
      dispatch(materialsAsyncActions[sendMaterials]({
        contentIds: checkedCards,
        emails: recipientInfo.emails,
        emailText: recipientInfo.emailHtml,
        templateId: recipientInfo.templateId as number,
        toSelf: recipientInfo.toSelf
      }));
    }
  }

  function handleChangeShareMode(mode: boolean) {
    setIsShareMode(mode);
    if (mode) {
      setCheckedCards(materialsContent.map(r => r.id));
    }
  }

  function handleDoneShare() {
    dispatch(setSendMaterialsStatus(false));
  }

  function handleChangeShareMaterials(materialsModel: IShareMaterials) {
    setRecipientInfo(materialsModel);
  }

  function handleOpenCard(cardId: number) {
    if (isShareMode) {
      if (checkedCards.includes(cardId)) {
        setCheckedCards(checkedCards.filter(item => item !== cardId));
      } else {
        setCheckedCards([...checkedCards, cardId]);
      }
    } else {
      history(`/content#${cardId}`);
    }
  }

  function renderMaterialsCards() {
    return materialsContent.length === 0
      ? <>
        <NotFoundInfo
          title={intl.formatMessage({
            id: 'gritx.materials.notFound',
            defaultMessage: 'No Materials Added Yet'
          })}
          description={intl.formatMessage({
            id: 'gritx.materials.tapSave',
            defaultMessage: 'Tap "Add to materials" button when you view the article to save your materials here'
          })}
          image={NotFoundImg}
        />
      </>
      : <>
        <CardSection
          cards={isShareMode ? materialsContent.filter(item => item.material) : materialsContent}
          disabledButtons={isShareMode}
          checkedCards={checkedCards}
          onClickCard={handleOpenCard}
        />
      </>;
  }

  return <div className="materials container">
    {
      !loading.includes(getTranslations) && <>
        {loading.includes(sendMaterials) && <Loader nested/>}
        <h1 className="materials__title"><img src={DefaultIcon} alt="" className="materials__icon"/>
          <FormattedMessage
            id="gritx.materials.title"
            defaultMessage="Materials"
          />
        </h1>
        <div className="materials__share-line">
          <MaterialsShare
            isDisabledRemoveAll={!materialsContent.some(card => card.material)}
            isDisabledShare={!materialsContent.some(card => card.material)}
            isDisabledSend={!validateModel()}
            onSend={handleSendMaterials}
            onChange={handleChangeShareMaterials}
            onRemoveAll={removeAll}
            onShareMode={handleChangeShareMode}
            sendStatus={sendMaterialsStatus}
            onDone={handleDoneShare}
            checkedCards={checkedCards}
          />

        </div>
        {
          sendMaterialsStatus
            ? <div className="materials__done">
              <div className="materials__image-wrapper">
              </div>
              <p className="materials__done-text">
                <FormattedMessage
                  id="gritx.materials.success"
                  defaultMessage="Your materials were successfully sent!"
                />
              </p>
            </div>
            : <div className="materials__cards">
              {
                loading.includes(getMaterialsContent) || isLoadCards ? <Loader nested/> : renderMaterialsCards()}
            </div>
        }
      </>
    }
  </div>;
};
